import { useState, useCallback, useMemo, useEffect } from 'react';

const CART_STORAGE_KEY = 'iconnectstore_cart';

function loadCartFromStorage() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
}

/**
 * Cart item shape includes product metadata and item-level comment entries.
 */
export function useCart() {
  const [items, setItems] = useState(loadCartFromStorage);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product) => {
    setItems((current) => {
      const existing = current.find((item) => item.id === product._id);
      if (existing) {
        return current.map((item) =>
          item.id === product._id
            ? {
                ...item,
                qty: item.qty + 1,
                sku: product.sku || item.sku || '',
                unitOfMeasurement: product.unitOfMeasurement || item.unitOfMeasurement || 'pc',
                barcode: product.barcode || item.barcode || '',
                posComments: Array.isArray(product.posComments) ? [...product.posComments] : (item.posComments || []),
                commentEntries: item.commentEntries || [],
              }
            : item
        );
      }
      return [
        ...current,
        {
          id: product._id,
          name: product.name,
          price: Number(product.price) || 0,
          image: product.image || '',
          sku: product.sku || '',
          unitOfMeasurement: product.unitOfMeasurement || 'pc',
          barcode: product.barcode || '',
          posComments: Array.isArray(product.posComments) ? [...product.posComments] : [],
          commentEntries: [],
          qty: 1,
        },
      ];
    });
  }, []);

  const updateItemDiscount = useCallback((id, discountPercent, discountFixed) => {
    setItems((current) =>
      current.map((item) =>
        item.id === id ? { ...item, discountPercent: discountPercent ?? 0, discountFixed: discountFixed ?? 0 } : item
      )
    );
  }, []);

  const updateItemComments = useCallback((id, commentEntries) => {
    const nextComments = (Array.isArray(commentEntries) ? commentEntries : [])
      .map((entry) => ({
        text: String(entry?.text || '').trim(),
        fromProduct: entry?.fromProduct === true,
      }))
      .filter((entry) => entry.text);
    setItems((current) =>
      current.map((item) =>
        item.id === id ? { ...item, commentEntries: nextComments } : item
      )
    );
  }, []);

  const updateItemProductComments = useCallback((id, posComments) => {
    const nextComments = [...new Set(
      (Array.isArray(posComments) ? posComments : [])
        .map((comment) => String(comment || '').trim())
        .filter(Boolean)
    )];
    setItems((current) =>
      current.map((item) =>
        item.id === id ? { ...item, posComments: nextComments } : item
      )
    );
  }, []);

  const updateQty = useCallback((id, nextQty) => {
    if (nextQty <= 0) {
      setItems((current) => current.filter((item) => item.id !== id));
      return;
    }
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, qty: nextQty } : item))
    );
  }, []);

  const removeItem = useCallback((id) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const count = useMemo(
    () => items.reduce((sum, item) => sum + item.qty, 0),
    [items]
  );

  const total = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.qty, 0),
    [items]
  );

  return {
    items,
    addItem,
    updateQty,
    updateItemDiscount,
    updateItemComments,
    updateItemProductComments,
    removeItem,
    clearCart,
    count,
    total,
  };
}
